import { LinkCard, LinkCardProps } from "@@/components/Card/LinkCard";
import Scroller, { ScrollerItem } from "@@/components/Scroller";
import { SideHeader } from "@@/components/SideHeader";
import Box from "@@/elements/Box";
import Container from "@@/elements/Container";
import Typography from "@@/elements/Typography";
import OptimizelyBlock, {
  OptimizelyBlockProps,
} from "@@/integrations/optimizly/Block";
import EpiContent from "@@/integrations/optimizly/Content";
import EpiProperty from "@@/integrations/optimizly/Property";
import { LinkData } from "@@/models/linkdata";
import cn from "classnames";
import { useEffect, useState } from "react";
import { TokenValue as bps } from "../../../design-tokens/breakpoints";
import styles from "../cardContainer.module.scss";

export type LinkCardContainerProperties = {
  header?: string;
  intro?: string;
  link?: LinkData;
  label?: string;
  headerVariant?: "left" | "center";
  backgroundColor?: "beige" | "white";
};

export type LinkCardContainerOwnProps = {
  cards: LinkCardProps[];
  sliderDesktop?: boolean;
};

export type LinkCardContainerProps = LinkCardContainerOwnProps &
  LinkCardContainerProperties;

const LinkCardContainer = ({
  header,
  intro,
  link,
  label,
  cards,
  sliderDesktop,
  headerVariant = "center",
  backgroundColor,
}: LinkCardContainerProps) => {
  const [cardLength, setCardLength] = useState<number>(3);
  const [slider, setSlider] = useState<boolean>(false);
  const [shouldShowSideHeader, setShouldShowSideHeader] = useState<boolean>(false);
  
  const getBackgroundColor = (color: string) => {
    switch (color) {
    case "beige":
      return "ComplementaryBeigeTint03";
    default:
      return "White";
    }
  };
  useEffect(() => {
    if (cards.length === 2) {
      setCardLength(2);
    } else setCardLength(3);

    const checkLayout = () => {
      if (sliderDesktop && window.innerWidth >= bps.Medium) {
        setSlider(true);
      } else setSlider(false);

      setShouldShowSideHeader(
        sliderDesktop && 
        window.innerWidth >= bps.Large && 
        headerVariant === "left",
      );
    };
    checkLayout();

    if (typeof window !== "undefined") {
      window.addEventListener("resize", checkLayout);

      return () => {
        window.removeEventListener("resize", checkLayout);
      };
    }
  }, [sliderDesktop, headerVariant]);

  const classNames = cn(styles.container, {
    [(styles as any)[`container_${cardLength}`]]: cardLength,
  });

  if (shouldShowSideHeader) {
    return (
      <SideHeader
        header={header}
        label={label}
        link={link}
        backgroundColor={backgroundColor}>
        <Scroller
          fullPageStretch
          sideHeader >
          {cards?.map((card, index) => (
            <ScrollerItem 
              key={index}>
              <Box className={styles["product-card__scroller-item"]}>
                <LinkCard
                  header={card.header}
                  color={card.color}
                  image={card.image}
                  link={card.link}
                />
              </Box>
            </ScrollerItem>
          ))}
        </Scroller>
      </SideHeader>
    );
  }
  return (
    <Box
      component="section"
      backgroundColor={getBackgroundColor(backgroundColor)}
      className={styles.block}>
      <Container component="div">
        <Box
          width="full"
          alignItems="center"
          flexDirection="column"
          textAlign="center"
        >
           {headerVariant === "left" && label && (
            <EpiProperty<LinkCardContainerProperties> name="label">
              <div >
                <Typography
                  textcolor="dark"
                  variant="pretitle"
                  asElement="div">
                  {label}
                </Typography>
                <hr classname={styles.center_hr} />
              </div>
            </EpiProperty>
          )}
          {header && (
            <EpiProperty<LinkCardContainerProperties> name="header">
              <Typography
                mb={intro ? 8 : [10, 12, 14]}
                variant="heading1"
                asElement="h2"
              >
                {header}
              </Typography>
            </EpiProperty>
          )}
          {headerVariant === "center" && intro && (
            <EpiProperty<LinkCardContainerProperties> name="intro">
              <Typography
                mb={[10, 12, 14]}
                variant="intro">
                {intro}
              </Typography>
            </EpiProperty>
          )}
        </Box>
        {!slider && (
          <EpiContent>
            <Box className={classNames}>
              {cards?.map((card, index) => (
                <LinkCard
                  key={index}
                  header={card.header}
                  color={card.color}
                  image={card.image}
                  link={card.link}
                  fixedHeight
                />
              ))}
            </Box>
          </EpiContent>
        )}
        {slider && (
          <EpiContent>
            <Box>
              <Scroller fullPageStretch >
                {cards?.map((card, index) => (
                  <ScrollerItem 
                    key={index}>
                    <Box className={styles["product-card__scroller-item"]}>
                      <LinkCard
                        header={card.header}
                        color={card.color}
                        image={card.image}
                        link={card.link}
                      />
                    </Box>
                  </ScrollerItem>
                ))}
              </Scroller>
            </Box>
          </EpiContent>
        )}
      </Container>
    </Box>
  );
};

export type ProvidedLinkCardContainerProps =
  OptimizelyBlockProps<LinkCardContainerProperties> & LinkCardContainerProps;

const ProvidedLinkCardContainer = ({
  blockId,
  inEditMode,
  properties,
  ...rest
}: ProvidedLinkCardContainerProps) => (
  <OptimizelyBlock
    blockId={blockId}
    inEditMode={inEditMode}
    properties={properties}
  >
    {(optimizlyProperties) => (
      <LinkCardContainer
        {...rest}
        {...optimizlyProperties} />
    )}
  </OptimizelyBlock>
);

export default ProvidedLinkCardContainer;
